import { Button, Badge } from '@design-system';
import { Plus, Settings, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import {
  TableColumnSettingsDialog,
  useTableColumnsEditor,
} from '@/features/quote-and-apply-builder';

interface TableColumnsEditorProps {
  columns: PolicyFormTableColumn[];
  onChange: (columns: PolicyFormTableColumn[]) => void;
}

export function TableColumnsEditor({ columns, onChange }: TableColumnsEditorProps) {
  const {
    editingColumn,
    handleAddColumn,
    handleUpdateColumn,
    handleRemoveColumn,
    handleMoveColumn,
    getTypeLabel,
    handleOpenColumnSettings,
    handleCloseColumnSettings,
  } = useTableColumnsEditor({ columns, onChange });

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {columns.map((column, index) => {
          const hasConditions = !!(column.conditional?.visible || column.conditional?.required);
          const hasOptions = column.options && column.options.length > 0;

          return (
            <div
              key={column.id}
              className="flex items-center gap-2 rounded-lg border bg-white p-3"
            >
              <div className="flex flex-col gap-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMoveColumn(index, 'up')}
                  disabled={index === 0}
                >
                  <ChevronUp className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMoveColumn(index, 'down')}
                  disabled={index === columns.length - 1}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-950">{column.title}</span>
                  {column.required && <span className="text-xs text-red-500">*</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="font-mono text-gray-500">{column.id}</span>
                  <Badge variant="secondary">{getTypeLabel(column.type)}</Badge>
                  {column.validationType && (
                    <Badge variant="default">{column.validationType}</Badge>
                  )}
                  {hasOptions && <Badge variant="outline">{column.options!.length} options</Badge>}
                  {hasConditions && <Badge variant="success">Has conditions</Badge>}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpenColumnSettings(column)}
                  title="Column Settings"
                >
                  <Settings className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleRemoveColumn(column.id)}
                  title="Delete Column"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {columns.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center">
          <p className="text-sm text-gray-500">No columns defined yet</p>
          <p className="mt-1 text-xs text-gray-400">Add columns to define the table structure</p>
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={handleAddColumn}
      >
        <Plus className="mr-1 h-4 w-4" />
        Add Column
      </Button>

      {editingColumn && (
        <TableColumnSettingsDialog
          open={!!editingColumn}
          column={editingColumn}
          allColumns={columns}
          onSave={handleUpdateColumn}
          onClose={handleCloseColumnSettings}
        />
      )}
    </div>
  );
}
